/**
 * Bebilderte Rundgänge zum Durchklicken. Sie erscheinen nach der ersten Anmeldung und beim Dazubuchen eines Pakets, lassen sich
 * überspringen und sind jederzeit unter „Mein Konto“ und über Vero wieder aufrufbar. Die Bilder liegen unter /screens.
 */

export type GuideId = "start" | "rechnungen";

export interface GuideStep {
  title: string;
  text: string;
  /** Aufnahme vom PC (Dateiname ohne Endung in /screens). */
  image: string;
  /** Aufnahme vom Handy, falls es zu diesem Schritt eine gibt. */
  phoneImage?: string;
  imageAlt: string;
}

export interface Guide {
  id: GuideId;
  title: string;
  intro: string;
  /** Nur für Firmen mit diesem Zusatzmodul. */
  addon?: "billing";
  steps: GuideStep[];
}

export const GUIDES: Guide[] = [
  {
    id: "start",
    title: "Rundgang durch HandVero",
    intro: "In fünf kurzen Schritten sehen Sie, wie HandVero aufgebaut ist.",
    steps: [
      {
        title: "Ein Projekt je Auftrag",
        text: "Links stehen Ihre Projekte, rechts die Ordner dazu. Beim Anlegen wählen Sie eine Vorlage, zum Beispiel „Elektroinstallation“. Dann sind die Ordner gleich da.",
        image: "pc-projekt",
        phoneImage: "handy-projekte",
        imageAlt: "Projektansicht mit nummerierten Ordnern",
      },
      {
        title: "Fotos landen im richtigen Ordner",
        text: "Auf dem Handy den Ordner öffnen und die Kamera antippen. Das Foto liegt sofort im Ordner und ist am PC zu sehen, ohne Kabel und ohne Mail.",
        image: "pc-fotos",
        phoneImage: "handy-fotos",
        imageAlt: "Fotos in einem Projektordner",
      },
      {
        title: "Notizen und Absprachen",
        text: "Was mit dem Kunden besprochen wurde, halten Sie als Notiz oder Sprachmemo fest. Es hängt am Projekt und geht nicht verloren.",
        image: "pc-notizen",
        phoneImage: "handy-notizen",
        imageAlt: "Notizen eines Projekts",
      },
      {
        title: "Termine für das Team",
        text: "Termine mit Projekt, Ort und zuständiger Person. Wer möchte, verbindet seinen Outlook-Kalender.",
        image: "pc-kalender",
        phoneImage: "handy-kalender",
        imageAlt: "Kalender mit Terminen",
      },
      {
        title: "Vero hilft",
        text: "Das Maskottchen unten rechts erklärt jede Funktion und schlägt vor, wohin eine Notiz gehört. Fragen Sie es einfach, auch nach diesem Rundgang.",
        image: "pc-vero",
        phoneImage: "handy-vero",
        imageAlt: "Vero schlägt ein Projekt vor",
      },
    ],
  },
  {
    id: "rechnungen",
    title: "Neu: Rechnungen und Belege",
    intro: "So arbeiten Sie mit dem Paket „Rechnungen und Belege“.",
    addon: "billing",
    steps: [
      {
        title: "Angebote, Aufträge und Rechnungen",
        text: "Aus einem Angebot wird mit einem Klick ein Auftrag und daraus die Rechnung. Nummern laufen lückenlos, fertige Rechnungen sind gesperrt. Zahlungsziel und Texte wählen Sie aus Vorlagen.",
        image: "pc-rechnungen",
        imageAlt: "Liste der Rechnungen mit offenen und fälligen Beträgen",
      },
      {
        title: "Die Rechnung für Ihren Kunden",
        text: "Die PDF trägt Ihr Logo und Ihre Farbe. Auf Wunsch mit Zahl-QR-Code für die Banking-App. Vor dem Versand sehen Sie eine Vorschau, verschickt wird per Mail, Link oder WhatsApp.",
        image: "pdf-rechnung",
        imageAlt: "Beispiel einer Rechnung als PDF mit Zahl-QR-Code",
      },
      {
        title: "Übersicht über Ihr Geschäft",
        text: "Was bezahlt, offen oder überfällig ist, sehen Sie auf einen Blick. Mahnungen entstehen aus der Rechnung. Für die Steuerberatung gibt es den Export.",
        image: "pc-uebersicht",
        imageAlt: "Geschäftsübersicht mit Einnahmen und Ausgaben",
      },
      {
        title: "Ausgaben und Belege",
        text: "Eingangsrechnungen und Quittungen erfassen, Beleg als Foto oder PDF anhängen. Gebuchte Ausgaben sind gesperrt.",
        image: "pc-ausgaben",
        imageAlt: "Ausgabenliste mit Belegen",
      },
      {
        title: "Bank",
        text: "Kontoumsätze aus Ihrer Bank-Datei einlesen. HandVero ordnet Zahlungen den offenen Rechnungen zu, Sie bestätigen nur noch.",
        image: "pc-bank",
        imageAlt: "Bankumsätze mit zugeordneten Rechnungen",
      },
    ],
  },
];

export const guideById = (id: string): Guide | undefined => GUIDES.find((g) => g.id === id);
